import { createContext, useContext, type ReactNode, useMemo } from "react";
import type { TenantGlobalTrackingAttributes } from "@tenant/types";

const TrackingScopeContext = createContext<TrackingScopeContext>({
  trackingAttributes: {},
});

export interface TrackingScopeContext {
  trackingAttributes: Partial<TenantGlobalTrackingAttributes>;
}

export interface TrackingScopeProps<
  TTAttrs extends object = TenantGlobalTrackingAttributes,
> {
  children: ReactNode;
  trackingAttributes?: Partial<TTAttrs>;
}

export function useTrackingScopeContext(): TrackingScopeContext {
  return useContext(TrackingScopeContext);
}

export function TrackingScope<TTAttrs extends object>(
  props: TrackingScopeProps<TTAttrs>
) {
  const { children } = props;

  const parentContext = useContext(TrackingScopeContext);

  const trackingAttributes = useMemo<Partial<TenantGlobalTrackingAttributes>>(
    () => ({
      ...parentContext.trackingAttributes,
      ...props.trackingAttributes,
    }),
    [parentContext.trackingAttributes, props.trackingAttributes]
  );

  const providerValue = useMemo(
    () => ({
      trackingAttributes,
    }),
    [trackingAttributes]
  );

  return (
    <TrackingScopeContext.Provider value={providerValue}>
      {children}
    </TrackingScopeContext.Provider>
  );
}
